import { create } from "zustand";
import { 
    retrieveChargingConfiguration, 
    updateChargingConfiguration,
    getTransactionTypes,
    addChargingRange,
    deleteChargingRange,
    calculateCharge
} from "./actions";
import { ChargingConfiguration, TransactionType, TransactionTypeCharging, ChargingRange } from "./data";

type ChargingStoreProps = {
    loading: boolean;
    configuration: ChargingConfiguration | null;
    transactionTypes: TransactionType[];
    selectedTransactionType: string | null;
    selectedSubType: string | null;
    error: string;
    
    // Actions
    retrieveConfiguration: () => Promise<void>;
    retrieveTransactionTypes: () => Promise<void>;
    updateConfiguration: (data: TransactionTypeCharging) => Promise<void>;
    addRange: (transactionType: string, subType: string, range: ChargingRange) => Promise<void>;
    deleteRange: (transactionType: string, subType: string, rangeId: string) => Promise<void>;
    calculateChargeAmount: (transactionType: string, subType: string, amount: number) => Promise<number>;
    setSelectedTransactionType: (type: string | null, subType?: string | null) => void;
    clearError: () => void;
}

export const useChargingStore = create<ChargingStoreProps>((set, getState) => ({
    loading: false,
    configuration: null,
    transactionTypes: [],
    selectedTransactionType: null,
    selectedSubType: null,
    error: "",

    async retrieveConfiguration() {
        set({ loading: true, error: "" });
        try {
            const config = await retrieveChargingConfiguration();
            //console.log(config)
            set({ configuration: config, loading: false });
        } catch (error: any) {
            set({ loading: false, error: error.message || "Failed to retrieve charging configuration" });
        }
    },

    async retrieveTransactionTypes() {
        set({ loading: true, error: "" });
        try {
            const types = await getTransactionTypes();
            set({ transactionTypes: types, loading: false });
        } catch (error: any) {
            set({ loading: false, error: error.message || "Failed to retrieve transaction types" });
        }
    },

    async updateConfiguration(data: TransactionTypeCharging) {
        set({ loading: true, error: "" });
        try {
            await updateChargingConfiguration(data);
            
            // Update local configuration
            const state = getState();
            if (state.configuration) {
                // Handle case where configurations might be null/undefined
                const currentConfigs = state.configuration.configurations || [];
                const updatedConfigs = currentConfigs.map(config => {
                    if (config.transactionType === data.transactionType && 
                        config.subType === data.subType) {
                        return data;
                    }
                    return config;
                });
                
                // If not found, add it
                if (!updatedConfigs.find(c => c.transactionType === data.transactionType && c.subType === data.subType)) {
                    updatedConfigs.push(data);
                }
                
                set({ 
                    configuration: {
                        ...state.configuration,
                        configurations: updatedConfigs
                    },
                    loading: false 
                });
            } else {
                // If no configuration exists yet, create a new one
                set({
                    configuration: {
                        companyId: '',
                        configurations: [data],
                        lastModified: new Date().toISOString(),
                        modifiedBy: ''
                    },
                    loading: false
                });
            }
        } catch (error: any) {
            set({ loading: false, error: error.message || "Failed to update charging configuration" });
            throw error;
        }
    },

    async addRange(transactionType: string, subType: string, range: ChargingRange) {
        set({ loading: true, error: "" });
        try {
            await addChargingRange(transactionType, subType, range);
            
            // Refresh configuration
            await getState().retrieveConfiguration();
        } catch (error: any) {
            set({ loading: false, error: error.message || "Failed to add charging range" });
            throw error;
        }
    },

    async deleteRange(transactionType: string, subType: string, rangeId: string) {
        set({ loading: true, error: "" });
        try {
            await deleteChargingRange(transactionType, subType, rangeId);
            
            // Refresh configuration
            await getState().retrieveConfiguration();
        } catch (error: any) {
            set({ loading: false, error: error.message || "Failed to delete charging range" });
            throw error;
        }
    },

    async calculateChargeAmount(transactionType: string, subType: string, amount: number) {
        try {
            const result = await calculateCharge(transactionType, subType, amount);
            return result.charge;
        } catch (error: any) {
            set({ error: error.message || "Failed to calculate charge" });
            return 0;
        }
    },

    setSelectedTransactionType(type: string | null, subType: string | null = null) {
        set({ selectedTransactionType: type, selectedSubType: subType });
    },

    clearError() {
        set({ error: "" });
    }
}));